import { useState, useEffect } from 'react'
import api from '../../lib/axios'
import toast from 'react-hot-toast'
import clsx from 'clsx'

const PLAN_COLORS = {
  free:       { ring: 'border-white/10',       badge: 'text-white/40 bg-white/6',          cta: 'bg-white/8 text-white/60 hover:bg-white/12' },
  pro:        { ring: 'border-purple-500/40',   badge: 'text-purple-300 bg-purple-500/15',  cta: 'btn-specze text-white' },
  team:       { ring: 'border-blue-500/40',     badge: 'text-blue-300 bg-blue-500/15',      cta: 'bg-blue-500/20 border border-blue-500/35 text-blue-300 hover:bg-blue-500/30' },
  enterprise: { ring: 'border-amber-500/40',    badge: 'text-amber-300 bg-amber-500/15',    cta: 'bg-amber-500/20 border border-amber-500/35 text-amber-300 hover:bg-amber-500/30' },
}

export default function Billing() {
  const [data, setData]         = useState(null)
  const [loading, setLoading]   = useState(true)
  const [invoices, setInvoices] = useState([])
  const [cancelling, setCancelling] = useState(false)
  const [tab, setTab]           = useState('overview') // overview | plans | invoices

  useEffect(() => {
    Promise.all([
      api.get('/billing'),
      api.get('/billing/invoices').catch(() => ({ data: [] })),
    ]).then(([billingRes, invoiceRes]) => {
      setData(billingRes.data)
      setInvoices(invoiceRes.data)
    }).finally(() => setLoading(false))
  }, [])

  const openStripePortal = async () => {
    const { data: res } = await api.get('/billing/portal')
    window.open(res.url, '_blank')
  }

  const cancelSubscription = async () => {
    if (!confirm("Cancel your subscription? You'll keep access until the end of the billing period.")) return
    setCancelling(true)
    try {
      await api.delete('/billing/subscribe')
      toast.success('Subscription cancelled. Access continues until period end.')
      const { data: fresh } = await api.get('/billing')
      setData(fresh)
    } catch { toast.error('Could not cancel.') }
    finally { setCancelling(false) }
  }

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="w-8 h-8 border-2 border-purple-500/30 border-t-purple-500 rounded-full animate-spin" />
    </div>
  )

  const plan   = data?.plan ?? 'free'
  const colors = PLAN_COLORS[plan] ?? PLAN_COLORS.free
  const usage  = data?.usage ?? {}
  const sub    = data?.subscription

  return (
    <div className="max-w-4xl mx-auto px-6 py-8 space-y-6">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display font-bold text-white text-2xl">Billing</h1>
          <p className="text-sm text-white/40 mt-1">Manage your plan, usage, and payment method</p>
        </div>
        <button onClick={openStripePortal}
          className="px-4 py-2 rounded-xl bg-white/6 border border-white/10 text-white/60 hover:text-white hover:bg-white/10 text-xs font-semibold transition-all">
          Manage Payment Method
        </button>
      </div>

      {/* Current plan card */}
      <div className={clsx('rounded-2xl border p-6', colors.ring, 'bg-white/[0.02]')}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <span className={clsx('text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full', colors.badge)}>
              {data?.plan_name} Plan
            </span>
            <div className="mt-3 flex items-baseline gap-2">
              {sub ? (
                <>
                  <span className="text-white/60 text-sm">
                    {sub.status === 'active' ? `Renews ${new Date(sub.renews_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}` : sub.status}
                  </span>
                  {sub.ends_at && (
                    <span className="text-red-400 text-xs">Cancels {new Date(sub.ends_at).toLocaleDateString()}</span>
                  )}
                </>
              ) : (
                <span className="text-white/40 text-sm">Free plan — no subscription</span>
              )}
            </div>
            {data?.payment_method && (
              <p className="text-xs text-white/30 mt-1">
                {data.payment_method.type} ending in {data.payment_method.last_four}
              </p>
            )}
          </div>

          {sub && sub.status === 'active' && !sub.ends_at && (
            <button onClick={cancelSubscription} disabled={cancelling}
              className="text-xs text-red-400/60 hover:text-red-400 transition-colors">
              {cancelling ? 'Cancelling…' : 'Cancel plan'}
            </button>
          )}
          {sub?.ends_at && (
            <button onClick={async () => { await api.post('/billing/subscribe/resume'); const { data: f } = await api.get('/billing'); setData(f) }}
              className="text-xs text-emerald-400 hover:text-emerald-300 transition-colors">
              Resume subscription
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-white/8">
        {[['overview','Usage Overview'],['plans','Upgrade Plan'],['invoices',`Invoices (${invoices.length})`]].map(([t, label]) => (
          <button key={t} type="button" onClick={() => setTab(t)}
            className={clsx('px-5 py-2.5 text-xs font-semibold border-b-2 transition-colors',
              tab === t ? 'text-purple-400 border-purple-400' : 'text-white/35 border-transparent hover:text-white/60')}>
            {label}
          </button>
        ))}
      </div>

      {/* Usage Overview */}
      {tab === 'overview' && (
        <div className="space-y-3">
          {Object.entries(usage).map(([key, val]) => {
            const label  = key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
            const pct    = val.limit ? Math.min(100, Math.round((val.used / val.limit) * 100)) : 0
            const over90 = pct >= 90
            return (
              <div key={key} className="glass-card p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-white">{label}</span>
                  <span className={clsx('text-xs font-bold tabular-nums', over90 ? 'text-red-400' : 'text-white/50')}>
                    {val.used?.toLocaleString()} {val.limit ? `/ ${val.limit === 2147483647 ? '∞' : val.limit.toLocaleString()}` : ''}
                  </span>
                </div>
                {val.limit && val.limit !== 2147483647 && (
                  <div className="h-1.5 bg-white/8 rounded-full overflow-hidden">
                    <div className={clsx('h-full rounded-full transition-all',
                      over90 ? 'bg-red-500' : pct >= 70 ? 'bg-amber-500' : 'bg-purple-500')}
                      style={{ width: `${pct}%` }} />
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* Upgrade plans */}
      {tab === 'plans' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {(data?.plans_available ?? []).map(p => (
            <div key={p.key} className={clsx('glass-card p-5 rounded-2xl border', PLAN_COLORS[p.key]?.ring ?? 'border-white/8')}>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="font-bold text-white text-lg">{p.name}</p>
                  <p className="text-white/40 text-sm">
                    {p.price_monthly === 0 ? 'Custom pricing' : `$${p.price_monthly}/mo`}
                  </p>
                </div>
                <span className={clsx('text-[9px] font-bold uppercase tracking-widest px-2 py-1 rounded-full', PLAN_COLORS[p.key]?.badge)}>
                  {p.key}
                </span>
              </div>
              <ul className="space-y-1.5 mb-5">
                {(p.highlight_features ?? []).map(f => (
                  <li key={f} className="flex items-center gap-2 text-xs text-white/60">
                    <span className="text-emerald-400">✓</span>{f}
                  </li>
                ))}
              </ul>
              <button
                onClick={openStripePortal}
                className={clsx('w-full py-2.5 rounded-xl text-xs font-bold transition-all', PLAN_COLORS[p.key]?.cta)}>
                {p.price_monthly === 0 ? 'Contact Sales' : `Upgrade to ${p.name}`}
              </button>
            </div>
          ))}
          {(data?.plans_available ?? []).length === 0 && (
            <div className="col-span-2 text-center py-10 text-white/30 text-sm">
              You're on the highest plan. 🎉
            </div>
          )}
        </div>
      )}

      {/* Invoices */}
      {tab === 'invoices' && (
        <div className="space-y-2">
          {invoices.length === 0 ? (
            <div className="text-center py-10 text-white/30 text-sm">No invoices yet.</div>
          ) : invoices.map(inv => (
            <div key={inv.id} className="glass-card px-4 py-3 flex items-center gap-4">
              <div className="flex-1">
                <p className="text-sm text-white font-medium">{new Date(inv.date).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</p>
                <p className="text-xs text-white/40">{inv.status}</p>
              </div>
              <span className="text-sm font-bold text-white">{inv.total}</span>
              {inv.download && (
                <a href={inv.download} target="_blank" rel="noreferrer"
                  className="text-xs text-purple-400 hover:text-purple-300 transition-colors">
                  Download
                </a>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
